import { UseQueryResult } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useRootState, useRootDispatch } from 'store/StateProvider';
import { saveFirebirdData } from 'store/slices/actions';
import { dailyCreditUsage } from 'types';
import { calculateMedian } from 'utils/utils';
import { useAPIQuery } from '../useAPIQuery';

export function useGetDailyCreditUsage({ selectedProductCustomerId, protocol, endpoint }, enabled = false) {
    const dispatch = useRootDispatch();
    const {
        sourcesData: {
            customerDetails: {
                instanceList,
                customerSfdcOemId,
                dailyAvgCreditConsumption,
                ignorePrepopulateCreditBalance,
            },
        },
    } = useRootState();

    const url = `/creditsizer/firebird/v2/credits/usage`;
    const method = 'GET';

    const handleSelect = (data: { result: dailyCreditUsage[]; statusCode: number; success: boolean }) => {
        if (data.result?.length > 0) {
            const [{ dailyCreditStats }] = data.result;
            const creditsConsumedArray = dailyCreditStats.map(({ creditsConsumed }) => +creditsConsumed);
            const calculatedDailyAvgCreditConsumption = calculateMedian(creditsConsumedArray);
            let existingData = {};
            if (!+dailyAvgCreditConsumption && !ignorePrepopulateCreditBalance) {
                existingData = {
                    dailyAvgCreditConsumption: `${
                        calculatedDailyAvgCreditConsumption || calculatedDailyAvgCreditConsumption === 0
                            ? calculatedDailyAvgCreditConsumption.toFixed(2)
                            : ''
                    }`,
                };
            }

            const parsedData = {
                ...existingData,
                firebirdData: {
                    dailyAvgCreditConsumption: `${
                        calculatedDailyAvgCreditConsumption || calculatedDailyAvgCreditConsumption === 0
                            ? calculatedDailyAvgCreditConsumption.toFixed(2)
                            : ''
                    }`,
                },
            };
            return { ...data, result: parsedData };
        }
        return data;
    };
    const phoenixCloudUrl = instanceList.find((obj) => obj.instanceId === selectedProductCustomerId)?.cloudName;
    const currentDay = parseInt(`${new Date().valueOf() / (60 * 60 * 24 * 1000)}`, 10);

    const payload = {
        dayStart: currentDay - 90,
        dayEnd: currentDay,
        cloudName: phoenixCloudUrl,
        instanceIds: [selectedProductCustomerId],
        amountsAsCredits: true,
        pageNumber: 1,
        pageSize: 100,
    };
    const query: UseQueryResult<{ result: {}; statusCode: number; success: boolean }, Error> = useAPIQuery({
        key: ['dailyCreditUsage', customerSfdcOemId, selectedProductCustomerId],
        method,
        payload,
        url,
        baseUrl: `${protocol}://${endpoint}`,
        select: handleSelect,
        enabled,
        options: {
            staleTime: 24 * 60 * 60 * 1000, // 24 hour stale time to avoid refetching the same API
        },
    });

    useEffect(() => {
        if (query?.data?.success && !query.isError && enabled) {
            dispatch(saveFirebirdData(query.data.result));
        }
    }, [query.data, query.isError, enabled]);
    return query;
}
